import { useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Info, MapPinned, Route, Send } from 'lucide-react'
import { Button, Field, Divider } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import { ORDER_KINDS, REMOTE_ID, UAS_CLASSES, UAS_CLASS_LIST, validateDeclaration } from '../../lib/classes'
import type { ValidationIssue } from '../../lib/classes'
import { formatDecimal, isValidCoord, routeWidthM } from '../../lib/geo'
import type { FlightDeclaration, Los, UasClass } from '../../types'

/* ============================================================================
 * ФОРМА ИНТЕГРАЦИИ С СППИ (система планирования и подачи заявок)
 *
 * Требования ТЗ:
 *   • район задаётся В КООРДИНАТАХ (WGS-84);
 *   • высоты — В МЕТРАХ (не футах);
 *   • маршрут задаётся координатами;
 *   • проверка ширины маршрута: в классах H и G — НЕ БОЛЕЕ 4 КМ;
 *   • уведомительный порядок (класс Н) против разрешительного (у границ
 *     и в контролируемых зонах);
 *   • класс Remote ID — ЭРА-ГЛОНАСС, 1090 МГц, ≥ 2 Вт, WGS-84.
 * ========================================================================= */

/** Разбор координат из текстового поля: «широта, долгота» по строкам */
export function parseCoords(text: string): Array<[number, number]> {
  const out: Array<[number, number]> = []
  for (const rawLine of text.split(/[\n;]+/)) {
    const line = rawLine.trim()
    if (!line) continue
    // Поддерживаем и «lat, lon», и «lat lon», и формат с запятой-разделителем дробей
    const parts = line.split(/[,\s]+/).filter(Boolean)
    if (parts.length < 2) continue
    const lat = Number(parts[0])
    const lon = Number(parts[1])
    if (isValidCoord(lat, lon)) out.push([lon, lat])
  }
  return out
}

/** Разбор площади: нечётное число точек означает открытый контур — замыкаем */
export function parseArea(text: string): Array<[number, number]> {
  const pts = parseCoords(text)
  if (pts.length >= 3) {
    const first = pts[0]
    const last = pts[pts.length - 1]
    if (first[0] !== last[0] || first[1] !== last[1]) pts.push(first)
  }
  return pts
}

export interface SppiFormValues {
  uasClass: UasClass
  altM: number
  massKg: number
  los: Los
  nearControlZone: boolean
  remoteIdEnabled: boolean
  areaText: string
  routeText: string
  operator: string
}

export const DEFAULT_SPP: SppiFormValues = {
  uasClass: 'Н',
  altM: 120,
  massKg: 25,
  los: 'VLOS',
  nearControlZone: false,
  remoteIdEnabled: true,
  areaText: '',
  routeText: '',
  operator: '',
}

export function SppiForm({
  onSubmit,
  submitting,
  lastSubmitted,
}: {
  onSubmit: (payload: Record<string, unknown>) => void
  submitting: boolean
  lastSubmitted: FlightDeclaration | null
}) {
  const [v, setV] = useState<SppiFormValues>(DEFAULT_SPP)
  const set = <K extends keyof SppiFormValues>(k: K, val: SppiFormValues[K]): void =>
    setV((prev) => ({ ...prev, [k]: val }))

  const area = useMemo(() => parseArea(v.areaText), [v.areaText])
  const route = useMemo(() => parseCoords(v.routeText), [v.routeText])
  const width = useMemo(() => (route.length >= 2 ? routeWidthM(route) : 0), [route])

  const profile = UAS_CLASSES[v.uasClass]
  const needsPermission = v.nearControlZone || profile.order === 'permission'

  // Живая проверка требований
  const issues = useMemo<ValidationIssue[]>(
    () =>
      validateDeclaration({
        uasClass: v.uasClass,
        altM: v.altM,
        route,
        nearBorderOrControlZone: v.nearControlZone,
        los: v.los,
        massKg: v.massKg,
        remoteIdEnabled: v.remoteIdEnabled,
      }),
    [v, route],
  )

  const hasError = issues.some((i) => i.level === 'error')
  const canSubmit = !hasError && area.length >= 3 && route.length >= 2 && v.operator.trim() !== ''

  return (
    <div className="space-y-2 p-2.5">
      {/* Сводка класса и требуемого порядка */}
      <div className="ut-stated ut-state-pending rounded-md border px-2.5 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-semibold">
            Класс «{v.uasClass}» — {profile.label}
          </div>
          <StateBadge state={needsPermission ? 'warning' : 'approved'}>
            {ORDER_KINDS[needsPermission ? 'permission' : 'notification'].label}
          </StateBadge>
        </div>
        <div className="mt-1 text-[10px] leading-snug text-ink-muted">
          {ORDER_KINDS[needsPermission ? 'permission' : 'notification'].description}
        </div>
        <div className="ut-metric mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-ink-muted">
          <span>Высота: {profile.minAltM}–{profile.maxAltM} м</span>
          <span>Масса: ≤ {profile.maxMassKg} кг</span>
          <span>{profile.los}</span>
          <span>
            Маршрут:{' '}
            {profile.maxRouteWidthM === null ? 'без ограничения' : `≤ ${profile.maxRouteWidthM / 1000} км`}
          </span>
        </div>
      </div>

      <Divider label="Параметры БВС" />

      <div className="grid grid-cols-2 gap-2">
        <Field label="Класс БВС" hint="Применяются Н, A, C, G, H (не B/D/E)">
          <select
            className="ut-select"
            value={v.uasClass}
            onChange={(e) => {
              const next = e.target.value as UasClass
              const p = UAS_CLASSES[next]
              setV((prev) => ({
                ...prev,
                uasClass: next,
                los: p.los,
                // Высота подстраивается под предел класса
                altM: Math.min(prev.altM, p.maxAltM),
                massKg: Math.min(prev.massKg, p.maxMassKg),
              }))
            }}
          >
            {UAS_CLASS_LIST.map((c) => (
              <option key={c} value={c}>
                {c} — {UAS_CLASSES[c].label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Высота полёта, м" hint="Высоты — в МЕТРАХ">
          <input
            type="number"
            className="ut-input ut-metric"
            min={0}
            max={profile.maxAltM}
            value={v.altM}
            onChange={(e) => set('altM', Number(e.target.value))}
          />
        </Field>

        <Field label="Масса, кг">
          <input
            type="number"
            className="ut-input ut-metric"
            min={0}
            step={0.5}
            value={v.massKg}
            onChange={(e) => set('massKg', Number(e.target.value))}
          />
        </Field>

        <Field label="Видимость">
          <select
            className="ut-select"
            value={v.los}
            onChange={(e) => set('los', e.target.value as Los)}
          >
            <option value="VLOS">VLOS — визуальная</option>
            <option value="EVLOS">EVLOS — вне визуальной</option>
            <option value="BVLOS">BVLOS — за пределами видимости</option>
          </select>
        </Field>
      </div>

      <Field label="Оператор / организация">
        <input
          className="ut-input"
          placeholder="ООО «Ромашка», ИНН 7700000000"
          value={v.operator}
          onChange={(e) => set('operator', e.target.value)}
        />
      </Field>

      <label className="flex cursor-pointer items-start gap-2 text-[11px]">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={v.nearControlZone}
          onChange={(e) => set('nearControlZone', e.target.checked)}
        />
        <span>
          Район рядом с государственной границей или в контролируемой зоне
          <span className="block text-[10px] text-ink-muted">
            Включает РАЗРЕШИТЕЛЬНЫЙ порядок вместо уведомительного
          </span>
        </span>
      </label>

      <Divider label="Геометрия района (WGS-84)" />

      <Field
        label="Район в координатах"
        hint={`Строки вида «широта, долгота». Задано точек: ${Math.max(0, area.length - (area.length >= 3 ? 1 : 0))}`}
        error={v.areaText.trim() !== '' && area.length < 3 ? 'Нужно не менее 3 точек' : undefined}
      >
        <textarea
          className="ut-textarea"
          rows={4}
          placeholder={'55.7558, 37.6173\n55.7658, 37.6173\n55.7658, 37.6273\n55.7558, 37.6273'}
          value={v.areaText}
          onChange={(e) => set('areaText', e.target.value)}
        />
      </Field>

      {area.length >= 3 && (
        <div className="ut-stated ut-state-approved rounded-md border px-2 py-1 text-[10px]">
          <MapPinned size={11} className="mr-1 inline" aria-hidden />
          Первая вершина: {formatDecimal(area[0][1], area[0][0])}
        </div>
      )}

      <Field
        label="Маршрут в координатах"
        hint={`Строки вида «широта, долгота». Точек: ${route.length}`}
        error={v.routeText.trim() !== '' && route.length < 2 ? 'Нужно не менее 2 точек' : undefined}
      >
        <textarea
          className="ut-textarea"
          rows={4}
          placeholder={'55.7558, 37.6173\n55.7600, 37.6250'}
          value={v.routeText}
          onChange={(e) => set('routeText', e.target.value)}
        />
      </Field>

      {/* ШИРИНА МАРШРУТА — критичное требование для классов G и H */}
      {route.length >= 2 && profile.maxRouteWidthM !== null && (
        <div
          className={`rounded-md border px-2 py-1.5 text-[11px] ut-stated ${
            width > profile.maxRouteWidthM ? 'ut-state-critical-error' : 'ut-state-approved'
          }`}
        >
          <div className="flex items-center gap-1.5 font-semibold">
            <Route size={12} aria-hidden />
            Ширина маршрута: {(width / 1000).toFixed(2)} км
            <span className="ml-auto ut-metric">
              предел {(profile.maxRouteWidthM / 1000).toFixed(0)} км
            </span>
          </div>
          {width > profile.maxRouteWidthM && (
            <div className="mt-0.5 flex items-start gap-1">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" aria-hidden />
              <span>
                Превышен предел 4 км для класса «{v.uasClass}» — маршрут должен быть сужен
                или выбран класс H с разрешительным порядком.
              </span>
            </div>
          )}
        </div>
      )}

      <Divider label="Идентификация" />

      <div className="ut-stated ut-state-approved rounded-md border px-2 py-1.5">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold">
            Remote ID · {REMOTE_ID.standard}
          </div>
          <StateBadge state={v.remoteIdEnabled ? 'approved' : 'critical-error'}>
            {v.remoteIdEnabled ? 'Включён' : 'Отключён'}
          </StateBadge>
        </div>
        <div className="ut-metric mt-1 flex flex-wrap gap-x-3 text-[10px] text-ink-muted">
          <span>{REMOTE_ID.frequencyMHz} МГц</span>
          <span>≥ {REMOTE_ID.minPowerW} Вт</span>
          <span>{REMOTE_ID.crs}</span>
        </div>
        <label className="mt-1.5 flex cursor-pointer items-center gap-2 text-[11px]">
          <input
            type="checkbox"
            checked={v.remoteIdEnabled}
            onChange={(e) => set('remoteIdEnabled', e.target.checked)}
          />
          Передавать идентификацию
        </label>
      </div>

      <Divider label="Результат проверки" />

      <div className="space-y-1">
        {issues.length === 0 ? (
          <div className="text-[11px] text-ink-muted">Замечаний нет.</div>
        ) : (
          issues.map((issue, i) => (
            <div
              key={i}
              className={`flex items-start gap-1.5 rounded px-1.5 py-1 text-[11px] ut-stated ${
                issue.level === 'error'
                  ? 'ut-state-critical-error'
                  : issue.level === 'warning'
                    ? 'ut-state-warning'
                    : 'ut-state-approved'
              }`}
            >
              {issue.level === 'error' ? (
                <AlertTriangle size={11} className="mt-0.5 shrink-0" aria-hidden />
              ) : issue.level === 'warning' ? (
                <AlertTriangle size={11} className="mt-0.5 shrink-0" aria-hidden />
              ) : (
                <Info size={11} className="mt-0.5 shrink-0" aria-hidden />
              )}
              <span>{issue.message}</span>
            </div>
          ))
        )}
      </div>

      {lastSubmitted && (
        <div className="ut-stated ut-state-pending rounded-md border px-2 py-1 text-[10px]">
          <CheckCircle2 size={11} className="mr-1 inline" aria-hidden />
          Последняя заявка: {lastSubmitted.id ?? 'без идентификатора'} ·{' '}
          {lastSubmitted.is_approved ? 'согласована' : 'на рассмотрении'}
        </div>
      )}

      <Button
        variant={hasError ? 'danger' : 'primary'}
        icon={Send}
        full
        disabled={!canSubmit || submitting}
        onClick={() =>
          onSubmit({
            uas_class: v.uasClass,
            alt_m: v.altM,
            mass_kg: v.massKg,
            los: v.los,
            order: needsPermission ? 'permission' : 'notification',
            near_control_zone: v.nearControlZone,
            remote_id: v.remoteIdEnabled ? REMOTE_ID.standard : null,
            remote_id_freq_mhz: REMOTE_ID.frequencyMHz,
            crs: REMOTE_ID.crs,
            operator: v.operator,
            area,
            route,
            route_width_m: Math.round(width),
            format: 'ГОСТ Р 71934-2025',
          })
        }
      >
        {submitting ? 'Отправка…' : 'Подать заявку в СППИ'}
      </Button>

      {!canSubmit && (
        <div className="text-[10px] text-ink-muted">
          Заполните оператора, укажите район (≥3 точек) и маршрут (≥2 точек), устраните ошибки.
        </div>
      )}
    </div>
  )
}
